import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";
import { Building2, ShieldCheck, Trash2, FileText, ArrowLeft, ChevronRight } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import "../components/CompanyManagementMobile.css";

export default function CompanySettings() {
  const [, params] = useRoute("/company/:id/manage");
  const id = params?.id || "";
  const { user, setUser, loading } = useAuth();
  const { t } = useI18n();
  const [, navigate] = useLocation();
  const [dashboard, setDashboard] = useState<Awaited<ReturnType<typeof api.companyDashboard>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [disputeBlocked, setDisputeBlocked] = useState(false);
  const [mobileViewport, setMobileViewport] = useState(() => window.matchMedia("(max-width: 639px)").matches);
  const confirmDialog = useRef<HTMLDialogElement>(null);
  const deleteTrigger = useRef<HTMLButtonElement>(null);
  const mobileConfirmationWasOpen = useRef(false);
  useEffect(() => {
    const viewport = window.matchMedia("(max-width: 639px)");
    const update = () => setMobileViewport(viewport.matches);
    viewport.addEventListener("change", update);
    return () => viewport.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    // Native modal focus/escape behavior applies only to the selected mobile
    // confirmation. A dispute refusal or a desktop resize closes this dialog.
    const dialog = confirmDialog.current;
    if (confirmDelete && mobileViewport && !disputeBlocked && !dashboard?.company.activeDisputeCount) {
      if (dialog && !dialog.open) { dialog.showModal(); mobileConfirmationWasOpen.current = true; }
    } else {
      if (dialog?.open) dialog.close();
      // The inline trigger is remounted after cancellation, so the browser's
      // native restoration target no longer exists. Restore it explicitly.
      if (mobileConfirmationWasOpen.current) {
        mobileConfirmationWasOpen.current = false;
        if (mobileViewport) deleteTrigger.current?.focus();
      }
    }
  }, [confirmDelete, mobileViewport, disputeBlocked, dashboard]);
  const scope = `${id}:${user?.id ?? "guest"}`;
  const currentScope = useRef(scope); currentScope.current = scope;
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    let active = true;
    setDashboard(null); setError(null); setBusy(false); setConfirmDelete(false); setDisputeBlocked(false);
    void api.companyDashboard(id).then(value => {
      if (!active) return;
      if (value?.company) setDashboard(value);
      else setError("Business settings are unavailable. Sign in again and retry.");
    }).catch(failure => {
      if (active) setError(failure instanceof Error ? failure.message : "Could not load business settings.");
    });
    return () => { active = false; };
  }, [id, user?.id, loading, navigate]);

  // [business.deletion](../../../docs/product/businesses.md#business-deletion): deletion remains owner-controlled and keeps the server's active-dispute
  // refusal. A completed request for a previous account/business cannot navigate this view.
  async function removeBusiness() {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const removed = await api.deleteCompany(id);
      if (!mounted.current || currentScope.current !== scope) return;
      setUser(removed.user); navigate("/dashboard");
    } catch (failure: unknown) {
      if (!mounted.current || currentScope.current !== scope) return;
      if (failure instanceof ApiError && failure.code === "active_dispute") { setDisputeBlocked(true); setConfirmDelete(false); }
      setError(failure instanceof Error ? failure.message : "Could not delete the business.");
    } finally { if (mounted.current && currentScope.current === scope) setBusy(false); }
  }
  async function viewDisputes() {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const selected = await api.switchContext(`company:${id}`);
      if (!selected) throw new Error("Sign in again to view your disputes.");
      if (!mounted.current || currentScope.current !== scope) return;
      setUser(selected); navigate("/messages/disputes");
    } catch (failure: unknown) {
      if (mounted.current && currentScope.current === scope) setError(failure instanceof Error ? failure.message : "Could not open disputes.");
    } finally { if (mounted.current && currentScope.current === scope) setBusy(false); }
  }

  if (loading || (!dashboard && !error)) return <div className="container-x py-16 text-center text-mut" role="status">Loading business settings…</div>;
  if (!dashboard) return <div className="container-x py-16 text-center"><p role="alert" className="text-brand-red">{error}</p><Link href="/dashboard" className="btn-outline mt-4">Back to Dashboard</Link></div>;
  const company = dashboard.company;
  if (dashboard.myRole !== "owner") return <div className="container-x py-16 text-center"><p>{t("company.settings.ownerOnly")}</p><Link href={`/company/${id}/dashboard`} className="btn-outline mt-4">Business history</Link></div>;
  const hasActiveDispute = disputeBlocked || company.activeDisputeCount > 0;
  return <div className="container-x py-6 pb-24 max-w-3xl company-management-mobile company-settings-mobile">
    <Link href={`/company/${id}/dashboard`} className="cm-back text-sm text-mut hover:text-navy"><span className="inline-flex items-center gap-1 sm:hidden"><ArrowLeft />My company</span><span className="hidden sm:inline">← {company.name}</span></Link>
    <h1 className="cm-mobile-heading hidden">Settings</h1>
    <h1 className="cm-desktop-heading mt-4 text-2xl font-extrabold text-navy">{t("company.settings.title")}</h1>
    <p className="cm-desktop-heading mt-2 text-sm text-mut">{t("company.settings.ownerOnly")}</p>
    {!company.deletedAt ? <>
      <section className="cm-panel">
        <h2 className="sm:hidden">Manage your business</h2>
        <div className="cm-links mt-5 flex flex-wrap gap-2">
          <Link href={`/company/${id}/edit`} className="btn-outline min-h-10"><Building2 className="w-4 h-4" /><span className="flex-1">{t("company.dashboard.editProfile")}</span><ChevronRight className="h-4 w-4 sm:hidden" /></Link>
          <Link href={`/company/${id}/licenses`} className="btn-outline min-h-10"><ShieldCheck className="w-4 h-4" /><span className="flex-1">{t("company.dashboard.licenses")}</span><ChevronRight className="h-4 w-4 sm:hidden" /></Link>
        </div>
      </section>
      <div className="cm-panel cm-danger card p-5 mt-5 border-brand-red/30">
        <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-brand-red sm:hidden">Company deletion</p>
        <h2 className="font-bold text-brand-red">Delete company</h2>
        <p className="cm-desktop-heading mt-2 mb-4 text-sm text-mut">Remove {company.name}'s public page and postings. Transaction records remain available to you and the other parties. You can finish existing agreements and access its wallet under Business history. This cannot be undone.</p>
        <div className="sm:hidden"><p className="mt-2 text-xs leading-6 text-mut">Remove {company.name}'s public page and postings.</p><ul className="cm-impact"><li><Trash2 /><span>The public company page and postings are removed.</span></li><li><FileText /><span>Transaction records remain available to the parties involved.</span></li><li><ShieldCheck /><span>Existing agreements and the wallet remain accessible through Business history.</span></li></ul><p className="mb-4 text-xs font-semibold text-brand-red">This cannot be undone.</p></div>
      {hasActiveDispute ? <div className="space-y-3"><p role="status" className="text-sm text-navy">This business has an ongoing dispute. Resolve it before deleting.</p><button type="button" disabled={busy} onClick={() => void viewDisputes()} className="btn-outline">View your disputes</button><p className="text-xs text-mut">Only the recorded agreement parties can participate in a dispute.</p></div> : confirmDelete ? mobileViewport ? <dialog ref={confirmDialog} aria-labelledby="company-delete-title" className="cm-delete-dialog" onCancel={event => { event.preventDefault(); if (!busy) setConfirmDelete(false); }}>
          <h2 id="company-delete-title" className="text-xl font-bold text-navy">Delete company?</h2>
          <p className="mt-3 text-sm leading-6 text-mut">Delete {company.name} and retain transaction history? The public page and postings will be removed. This cannot be undone.</p>
          {error && <p role="alert" className="mt-3 text-sm text-brand-red">{error}</p>}
          <div className="mt-5 grid grid-cols-2 gap-2"><button type="button" disabled={busy} onClick={() => setConfirmDelete(false)} className="btn-outline">Cancel</button><button type="button" disabled={busy} onClick={() => void removeBusiness()} className="btn-red">{busy ? "Deleting…" : "Yes, delete"}</button></div>
        </dialog> : <div className="cm-confirmation flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-navy">Delete {company.name} and retain transaction history?</span>
          <button type="button" disabled={busy} onClick={() => void removeBusiness()} className="btn-red min-h-10">{busy ? "Deleting…" : "Yes, delete"}</button>
          <button type="button" disabled={busy} onClick={() => setConfirmDelete(false)} className="btn-outline min-h-10">Cancel</button>
        </div> : <button ref={deleteTrigger} type="button" onClick={() => setConfirmDelete(true)} className="btn-outline min-h-10 text-brand-red"><Trash2 className="w-4 h-4" />Delete company</button>}
      </div>
    </> : <div className="card p-5 mt-5"><p className="text-sm text-mut">This business profile is deleted. Its transaction records remain available under Business history.</p><Link href={`/company/${id}/dashboard`} className="btn-outline mt-4">Business history</Link></div>}
    {error && !(mobileViewport && confirmDelete) && <p role="alert" className="mt-3 text-sm text-brand-red">{error}</p>}
  </div>;
}
